// src/Dashboard/pages/user/Reglements/components/ReglementsTable.jsx

import React from "react";
import {
  FaBell,
  FaCheckCircle,
  FaCoins,
  FaCreditCard,
  FaLock,
  FaFileInvoice,
} from "react-icons/fa";

import {
  FALLBACK_IMAGE,
  canPayInInstallments,
  canShowPaymentButtons,
  formatDisplayDate,
  formatEUR,
  getArticleMessage,
  getArticleName,
  getStatusMeta,
  canGenerateInvoice,
} from "../utils/reglementsUtils";

export default function ReglementsTable({
  reglements,
  payingId,
  notifyingId,
  isAlreadyNotified,
  onPay,
  onInstallments,
  onNotify,
  onInvoice,
}) {
  return (
    <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Règlement
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Statut
              </th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Montant
              </th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {reglements.map((reglement) => {
              const name = getArticleName(reglement);
              const message = getArticleMessage(reglement);
              const statusMeta = getStatusMeta(reglement.statut_reglement);

              const isPaymentDisabled = reglement.autorisation_achat === false;
              const showPaymentButtons = canShowPaymentButtons(reglement);
              const showInstallments = canPayInInstallments(reglement);
              const alreadyNotified = isAlreadyNotified?.(reglement.id);
              const showInvoice = canGenerateInvoice(reglement);

              return (
                <tr
                  key={reglement.id}
                  className="transition hover:bg-slate-50 dark:hover:bg-slate-900/70"
                >
                  <td className="px-4 py-4">
                    <div className="flex min-w-[360px] items-center gap-4">
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                        <img
                          src={reglement.photoUrl || FALLBACK_IMAGE}
                          alt={name}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.src = FALLBACK_IMAGE;
                          }}
                        />
                      </div>

                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                          {reglement.saison}
                        </p>

                        <p className="mt-1 font-bold text-slate-900 dark:text-white">
                          {reglement.quantite} x {name}
                        </p>

                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Achat du {formatDisplayDate(reglement.date_achat)}
                        </p>

                        {message && (
                          <p className="mt-1 line-clamp-2 max-w-xl text-xs italic text-slate-600 dark:text-slate-300">
                            {message}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="space-y-2">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1 ${statusMeta.badge}`}
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${statusMeta.dot}`}
                        />
                        {statusMeta.label}
                      </span>

                      {isPaymentDisabled && showPaymentButtons && (
                        <span className="flex items-center gap-1 text-xs font-bold text-red-600 dark:text-red-300">
                          <FaLock />
                          Paiement désactivé
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-right">
                    <span className="text-xl font-bold text-slate-900 dark:text-white">
                      {formatEUR(reglement.prix_total)}
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex min-w-[280px] flex-wrap justify-end gap-2">
                      {showPaymentButtons && (
                        <>
                          <button
                            type="button"
                            onClick={() => onPay(reglement.id)}
                            disabled={
                              isPaymentDisabled || payingId === reglement.id
                            }
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-xs font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <FaCreditCard />
                            {payingId === reglement.id
                              ? "Redirection..."
                              : "Payer"}
                          </button>

                          {showInstallments && (
                            <button
                              type="button"
                              onClick={() => onInstallments(reglement.id)}
                              disabled={isPaymentDisabled}
                              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
                            >
                              <FaCoins />
                              Plusieurs fois
                            </button>
                          )}
                        </>
                      )}

                      {showInvoice && (
                        <button
                          type="button"
                          onClick={() => onInvoice?.(reglement)}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-xs font-bold text-white transition hover:bg-indigo-800"
                        >
                          <FaFileInvoice />
                          Facture
                        </button>
                      )}

                      {reglement.statut_reglement === "Réglé" &&
                        (alreadyNotified ? (
                          <span className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                            <FaCheckCircle />
                            Admin prévenu
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onNotify(reglement)}
                            disabled={notifyingId === reglement.id}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <FaBell />
                            {notifyingId === reglement.id
                              ? "Envoi..."
                              : "Prévenir"}
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
